import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Case, Dialogue, Rating, GeneratedQuestions } from '../types';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import DialogueBubble from '../components/Dialogue/DialogueBubble';
import AudioPlayer from '../components/Audio/AudioPlayer';
import QuestionsModal from '../components/Dialogue/QuestionsModal';
import { 
  ArrowLeftIcon,
  PlayIcon,
  SpeakerWaveIcon,
  StarIcon,
  PencilIcon,
  SparklesIcon,
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const CaseDetailPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [dialogue, setDialogue] = useState<Dialogue | null>(null);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isAutoRating, setIsAutoRating] = useState(false);
  const [audioFiles, setAudioFiles] = useState<any[]>([]);
  const [currentAudioIndex, setCurrentAudioIndex] = useState<number>(0);
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isImprovingDialogue, setIsImprovingDialogue] = useState(false);
  const [improveNotes, setImproveNotes] = useState('');
  const [latestRating, setLatestRating] = useState<Rating | null>(null);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedQuestions | null>(null);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [isAnalyzingAllEmotions, setIsAnalyzingAllEmotions] = useState(false);
  const [bulkAnalysisResults, setBulkAnalysisResults] = useState<any[]>([]);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0 });
  const [analyzedTurns, setAnalyzedTurns] = useState<Set<number>>(new Set());
  
  // Permission states
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  
  // New: Continue Dialogue & Version History
  const [showContinueModal, setShowContinueModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [userResponse, setUserResponse] = useState('');
  const [isContinuing, setIsContinuing] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [continueAsAI, setContinueAsAI] = useState(true); // true = AI generates, false = user inputs
  const [showContinueInput, setShowContinueInput] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>(''); // Who speaks next
  const [responseMode, setResponseMode] = useState<'select' | 'method' | 'ai' | 'manual'>('select'); // select speaker, choose method, ai generate, or manual input
  
  // Edit Case Info
  const [showEditCaseModal, setShowEditCaseModal] = useState(false);
  const [editedCaseInfo, setEditedCaseInfo] = useState({
    title: '',
    patientProfile: {} as any,
    scenario: '',
    familyStatement: '',
    clinicianObjectives: [] as string[]
  });
  const [isPublishing, setIsPublishing] = useState(false);
  const [isInstructor, setIsInstructor] = useState(false);
  const { user } = useAuth();
  const [isTogglingGold, setIsTogglingGold] = useState(false);
  const [isSettingLevel, setIsSettingLevel] = useState(false);

  // Check user role on mount and when user changes
  useEffect(() => {
    const role = user?.role;
    const instructorOrAdmin = role === 'INSTRUCTOR' || role === 'ADMIN';
    console.log('User from auth:', user);
    console.log('Is instructor/admin?', instructorOrAdmin, 'Role:', role);
    setIsInstructor(instructorOrAdmin);
  }, [user]);

  // Helper function to detect medical professionals using multiple methods
  const isMedicalProfessional = (speaker: string): boolean => {
    if (!dialogue || !dialogue.participants) return false;
    
    // Use participant data for robust detection (same as backend)
    const participants = dialogue.participants as any[];
    const participant = participants.find((p: any) => {
      const pName = (p.name || '').trim();
      const speakerTrimmed = speaker.trim();
      return pName === speakerTrimmed || pName.toLowerCase() === speakerTrimmed.toLowerCase();
    });
    
    // If found in participants, check their role
    if (participant && participant.role) {
      const roleLower = participant.role.toLowerCase();
      
      // Medical professional roles
      const medicalRoles = [
        'doctor', 'physician', 'nurse', 'clinician', 'attending', 'resident',
        'medical student', 'intern', 'fellow', 'therapist', 'counselor',
        'social worker', 'chaplain', 'oncologist', 'cardiologist', 'neurologist',
        'pediatrician', 'surgeon', 'psychiatrist', 'psychologist', 'specialist',
        'consultant', 'healthcare provider', 'medical professional', 'care team member'
      ];
      
      if (medicalRoles.some(role => roleLower.includes(role))) {
        return true;
      }
      
      // Family/patient roles
      const familyRoles = [
        'mother', 'father', 'parent', 'guardian', 'sibling', 'brother', 'sister',
        'grandmother', 'grandfather', 'grandparent', 'aunt', 'uncle', 'family',
        'patient', 'child', 'son', 'daughter', 'spouse', 'partner', 'friend',
        'relative', 'caregiver', 'legal guardian'
      ];
      
      if (familyRoles.some(role => roleLower.includes(role))) {
        return false;
      }
    }
    
    // Fallback: Use name pattern matching (specific only)
    const medicalTitleRegex = /^(dr\.?\s|doctor\s|prof\.?\s|professor\s)/i;
    if (medicalTitleRegex.test(speaker)) {
      return true;
    }
    
    const medicalCredentialRegex = /(,?\s+(md|do|dmd|dds|dvm|phd|pharmd|dpt|rn|lpn|cna|pa-c|np|crna)\.?)$/i;
    if (medicalCredentialRegex.test(speaker)) {
      return true;
    }
    
    const familyIndicatorRegex = /\((mother|father|parent|guardian|brother|sister|sibling|grandmother|grandfather|grandparent|aunt|uncle|friend|patient|child|son|daughter|spouse|partner|wife|husband|family member|relative|caregiver)\)/i;
    if (familyIndicatorRegex.test(speaker)) {
      return false;
    }
    
    // Default to family/patient (safer to show button than hide it)
    return false;
  };

  useEffect(() => {
    if (caseId) {
      // Check if caseId is "new" or invalid, redirect to cases list
      if (caseId === 'new' || !caseId) {
        navigate('/cases');
        return;
      }
      loadCaseData();
    }
  }, [caseId, navigate]);

  // Load cached emotion analyses when dialogue is available
  useEffect(() => {
    if (dialogue && caseId) {
      loadCachedEmotionAnalyses();
    }
  }, [dialogue, caseId]);

  const loadCachedEmotionAnalyses = async () => {
    if (!caseId || !dialogue || !dialogue.turns) return;
    
    try {
      // Load cached emotion analysis results for patient/family turns only
      const cachedResults = [];
      for (let i = 0; i < dialogue.turns.length; i++) {
        // Skip medical professionals
        if (isMedicalProfessional(dialogue.turns[i].speaker)) {
          continue;
        }
        
        try {
          const response = await apiService.getCachedEmotionAnalysis(caseId, dialogue.turns[i].turnNumber);
          if (response.success && response.emotionAnalysis) {
            cachedResults.push({
              turnIndex: i,
              turnNumber: dialogue.turns[i].turnNumber,
              speaker: dialogue.turns[i].speaker,
              text: dialogue.turns[i].line,
              emotionAnalysis: response.emotionAnalysis
            });
          }
        } catch (error) {
          // No cached analysis for this turn, continue
        }
      }
      
      if (cachedResults.length > 0) {
        setBulkAnalysisResults(cachedResults);
        
        // Mark analyzed turns
        const newAnalyzedTurns = new Set<number>();
        cachedResults.forEach(result => {
          newAnalyzedTurns.add(result.turnIndex);
        });
        setAnalyzedTurns(newAnalyzedTurns);
        
        console.log(`Loaded ${cachedResults.length} cached emotion analyses`);
      }
    } catch (error) {
      console.error('Failed to load cached emotion analyses:', error);
    }
  };

  const loadCaseData = async () => {
    try {
      setIsLoading(true);
      setPermissionError(''); // Clear previous permission errors
      
      // Load case (backend now returns transformed Case from UserCaseInstance)
      const caseDataResponse = await apiService.getCaseByCaseId(caseId!);
      
      // Extract dialogue from case (backend populates it)
      const dialogueData = caseDataResponse.dialogues && caseDataResponse.dialogues.length > 0 
        ? caseDataResponse.dialogues[0] 
        : null;
      
      // Load ratings and audio (these still work with caseId/instanceId)
      const [ratingsResponse, audioFilesResponse] = await Promise.all([
        apiService.getRatingsByDialogue(caseId!).catch(() => []),
        apiService.getAudioFilesByDialogue(caseId!).catch(() => [])
      ]);
      
      setCaseData(caseDataResponse);
      setDialogue(dialogueData);
      setRatings(ratingsResponse);
      
      // Check if this is not the owner (instructor/admin viewing student case)
      if (caseDataResponse.createdBy?.id !== user?.id) {
        setIsViewOnly(true);
      } else {
        setIsViewOnly(false);
      }
      
      // Load the most recent audio file if available
      if (audioFilesResponse && audioFilesResponse.length > 0) {
        const latestAudio = audioFilesResponse[0]; // Already sorted by createdAt desc
        console.log('Loading existing audio file:', latestAudio);
        
        try {
          const audioBlob = await apiService.getAudioFile(latestAudio.id);
          const audioUrl = URL.createObjectURL(audioBlob);
          setCurrentAudioUrl(audioUrl);
          console.log('Existing audio loaded successfully:', audioUrl);
        } catch (audioErr) {
          console.error('Failed to load existing audio file:', audioErr);
          // Don't show error to user, just log it
        }
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('This case could not be found. It may have been deleted.');
        // Redirect to cases list after a short delay
        setTimeout(() => navigate('/cases'), 3000);
      } else if (err.response?.status === 403) {
        setPermissionError(err.response?.data?.error || 'You do not have permission to view this case.');
        // Don't set error, so the page stays rendered with permission banner
        // Don't redirect - keep them on the page
      } else {
        setError(err.response?.data?.error || 'Failed to load case data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMakeTemplate = async () => {
    if (!caseData) return;
    
    try {
      setIsPublishing(true);
      await apiService.makeUserCaseIntoTemplate(caseData.caseId);
      await loadCaseData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create template');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleEditCaseInfo = () => {
    if (!caseData) return;
    
    setEditedCaseInfo({
      title: caseData.title || '',
      patientProfile: caseData.patientProfile || {},
      scenario: caseData.scenario || '',
      familyStatement: caseData.familyStatement || '',
      clinicianObjectives: caseData.clinicianObjectives || []
    });
    setShowEditCaseModal(true);
  };

  const handleSaveCaseInfo = async () => {
    if (!caseData) return;
    
    if (isViewOnly) {
      setError('You are viewing this case in read-only mode. You cannot make changes to student cases.');
      return;
    }
    
    try {
      await apiService.updateUserCaseInstance(caseData.caseId, editedCaseInfo);
      setShowEditCaseModal(false);
      await loadCaseData(); // Smooth refresh, no alert
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have permission to edit this case.');
      } else {
        setError(err.response?.data?.error || 'Failed to update case information');
      }
    }
  };

  const handleToggleGoldExample = async () => {
    if (!caseData || !caseData.caseId) return;
    
    try {
      setIsTogglingGold(true);
      // Use instanceId for user cases (which is what caseId contains)
      const result = await apiService.toggleUserCaseGoldExample(caseData.caseId);
      setCaseData({ ...caseData, isGoldExample: result.isGoldExample });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle gold example');
    } finally {
      setIsTogglingGold(false);
    }
  };

  const handleSetLearnerLevel = async (level: string) => {
    if (!caseData || !caseData.caseId) return;
    
    setIsSettingLevel(true);
    try {
      const learnerLevel = level === '' ? null : level;
      const result = await apiService.setLearnerLevel(caseData.caseId, learnerLevel as any);
      setCaseData({ ...caseData, learnerLevel: result.learnerLevel });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set learner level');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSettingLevel(false);
    }
  };

  const handleGenerateAudio = async () => {
    if (!dialogue) return;

    try {
      setIsGeneratingAudio(true);
      console.log('Starting audio generation for case:', caseId);
      
      const response = await apiService.generateAudio(caseId!);

      console.log('Audio generation successful, received data:', response);
      
      // Handle multiple audio files (3+ participants) or single file (2 participants)
      const files = response.files || [response];
      console.log(`Generated ${files.length} audio file(s)`);
      
      setAudioFiles(files);
      
      // Load first audio file by default
      if (files.length > 0 && files[0].id) {
        const firstAudioBlob = await apiService.getAudioFile(files[0].id);
        const audioUrl = URL.createObjectURL(firstAudioBlob);
        setCurrentAudioUrl(audioUrl);
        setCurrentAudioIndex(0);
        console.log('First audio URL created:', audioUrl);
      }
    } catch (err: any) {
      console.error('Audio generation failed:', err);
      setError(err.response?.data?.error || 'Failed to generate audio');
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const handleAutoRate = async () => {
    try {
      setIsAutoRating(true);
      const rating = await apiService.autoRateDialogue(caseId!);
      setRatings([rating, ...ratings]);
      setLatestRating(rating); // Set the latest rating for improve dialogue
    } catch (err: any) {
      // Handle specific error for already rated dialogue
      if (err.response?.status === 400 && err.response?.data?.error?.includes('already rated')) {
        setError('This dialogue has already been rated. You can view the existing rating below.');
      } else {
        setError(err.response?.data?.error || 'Failed to generate rating');
      }
    } finally {
      setIsAutoRating(false);
    }
  };

  const handleModifyDialogue = async (action: string, turnIndex: number, originalText: string, customText?: string, selectedOption?: string) => {
    if (isViewOnly) {
      setError('You are viewing this case in read-only mode and cannot modify student work.');
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    try {
      console.log('Modifying dialogue with:', { 
        caseId, 
        turnIndex, 
        action, 
        originalText, 
        customText, 
        selectedOption 
      });
      
      await apiService.modifyDialogueTurn(caseId!, turnIndex, {
        action: action as any,
        originalText,
        turnIndex,
        customText,
        selectedOption,
      });
      
      console.log('Dialogue modification successful, reloading...');
      
      // DON'T clear backend data - the text_hash system automatically handles different versions!
      // Old emotion data is preserved with old text_hash, new data uses new text_hash.
      
      // Clear frontend cached emotion analysis for the modified turn since text has changed
      const newAnalyzedTurns = new Set(analyzedTurns);
      newAnalyzedTurns.delete(turnIndex);
      setAnalyzedTurns(newAnalyzedTurns);
      
      // Remove from bulk analysis results
      setBulkAnalysisResults(prev => prev.filter(result => result.turnIndex !== turnIndex));
      
      // Reload dialogue to get updated version
      const updatedDialogue = await apiService.getDialogueByCaseId(caseId!);
      setDialogue(updatedDialogue);
      
      console.log('Dialogue reloaded:', updatedDialogue);
    } catch (err: any) {
      console.error('Failed to modify dialogue:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to modify this case.');
      } else {
        setError(err.response?.data?.error || 'Failed to modify dialogue');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleImproveDialogue = async () => {
    if (!latestRating) {
      setError('No rating available to improve dialogue');
      return;
    }

    try {
      setIsImprovingDialogue(true);
      const improvedDialogue = await apiService.improveDialogue(caseId!, {
        ratingData: latestRating,
        additionalNotes: improveNotes,
      });
      
      setDialogue(improvedDialogue);
      setImproveNotes(''); // Clear the notes after successful improvement
      setError(''); // Clear any previous errors
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to improve dialogue');
    } finally {
      setIsImprovingDialogue(false);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!dialogue) return;

    try {
      setIsGeneratingQuestions(true);
      setShowQuestionsModal(true);
      setError('');

      const questions = await apiService.generateQuestions(caseId!);
      setGeneratedQuestions(questions);
    } catch (error: any) {
      console.error('Failed to generate questions:', error);
      setError(error.response?.data?.error || 'Failed to generate questions');
      setGeneratedQuestions(null);
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const handleCloseQuestionsModal = () => {
    setShowQuestionsModal(false);
    setGeneratedQuestions(null);
  };

  const handleAnalyzeAllEmotions = async () => {
    if (!caseId || !dialogue) return;

    try {
      setIsAnalyzingAllEmotions(true);
      setError('');
      // Count only patient/family turns (not doctor turns)
      // Use same robust detection as above
      const participants = (dialogue.participants || []) as any[];
      
      const isMedicalProfessionalLocal = (speaker: string): boolean => {
        const participant = participants.find((p: any) => {
          const pName = (p.name || '').trim();
          const speakerTrimmed = speaker.trim();
          return pName === speakerTrimmed || pName.toLowerCase() === speakerTrimmed.toLowerCase();
        });
        
        if (participant && participant.role) {
          const roleLower = participant.role.toLowerCase();
          const medicalRoles = ['doctor', 'physician', 'nurse', 'clinician', 'attending', 'resident', 'medical student', 'intern', 'fellow', 'therapist', 'counselor', 'social worker', 'chaplain', 'oncologist', 'cardiologist', 'neurologist', 'pediatrician', 'surgeon', 'psychiatrist', 'psychologist', 'specialist', 'consultant', 'healthcare provider', 'medical professional', 'care team member'];
          if (medicalRoles.some(role => roleLower.includes(role))) return true;
          
          const familyRoles = ['mother', 'father', 'parent', 'guardian', 'sibling', 'brother', 'sister', 'grandmother', 'grandfather', 'grandparent', 'aunt', 'uncle', 'family', 'patient', 'child', 'son', 'daughter', 'spouse', 'partner', 'friend', 'relative', 'caregiver', 'legal guardian'];
          if (familyRoles.some(role => roleLower.includes(role))) return false;
        }
        
        if (/^(dr\.?\s|doctor\s|prof\.?\s|professor\s)/i.test(speaker)) return true;
        if (/(,?\s+(md|do|dmd|dds|dvm|phd|pharmd|dpt|rn|lpn|cna|pa-c|np|crna)\.?)$/i.test(speaker)) return true;
        if (/\((mother|father|parent|guardian|brother|sister|sibling|grandmother|grandfather|grandparent|aunt|uncle|friend|patient|child|son|daughter|spouse|partner|wife|husband|family member|relative|caregiver)\)/i.test(speaker)) return false;
        
        return false;
      };

      const patientFamilyTurns = dialogue.turns?.filter(turn => {
        return !isMedicalProfessionalLocal(turn.speaker);
      }) || [];
      
      setAnalysisProgress({ current: 0, total: patientFamilyTurns.length });

      const results = await apiService.analyzeAllEmotions(caseId, (progress) => {
        // Update progress in real-time
        setAnalysisProgress({
          current: progress.current,
          total: progress.total
        });
      });
      
      setBulkAnalysisResults(results.results);
      
      // Mark all analyzed turns as completed
      const newAnalyzedTurns = new Set(analyzedTurns);
      results.results.forEach((result: any) => {
        if (result.emotionAnalysis && !result.error) {
          newAnalyzedTurns.add(result.turnIndex);
        }
      });
      setAnalyzedTurns(newAnalyzedTurns);
    } catch (error: any) {
      console.error('Failed to analyze all emotions:', error);
      if (error.code === 'ECONNABORTED') {
        setError('Analysis timed out. This can happen with long conversations. Please try again or analyze individual turns.');
      } else {
        setError(error.response?.data?.error || 'Failed to analyze all emotions');
      }
    } finally {
      setIsAnalyzingAllEmotions(false);
      setAnalysisProgress({ current: 0, total: 0 });
    }
  };


  const handlePlayAudio = () => {
    setIsPlaying(!isPlaying);
  };

  const handleStopAudio = () => {
    setIsPlaying(false);
    if (currentAudioUrl) {
      URL.revokeObjectURL(currentAudioUrl);
      setCurrentAudioUrl('');
    }
  };

  const handleSwitchAudio = async (index: number) => {
    try {
      // Clean up current audio URL
      if (currentAudioUrl) {
        URL.revokeObjectURL(currentAudioUrl);
      }
      
      setIsPlaying(false);
      
      // Load new audio file
      const fileId = audioFiles[index]?.id;
      if (!fileId) {
        setError('Invalid audio file');
        return;
      }
      
      const audioBlob = await apiService.getAudioFile(fileId);
      const audioUrl = URL.createObjectURL(audioBlob);
      setCurrentAudioUrl(audioUrl);
      setCurrentAudioIndex(index);
    } catch (err: any) {
      setError('Failed to load audio file');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading case...</span>
        </div>
      </div>
    );
  }

  // Handle continue dialogue
  const handleContinueDialogue = async (asAI: boolean) => {
    if (!caseId || !dialogue?.turns || !selectedSpeaker) return;
    
    if (isViewOnly) {
      setError('You are viewing this case in read-only mode and cannot modify student work.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Validate user input if not AI
    if (!asAI && !userResponse.trim()) {
      setError('Please enter your response');
      return;
    }
    
    try {
      setIsContinuing(true);
      
      // Use the selected speaker from the dropdown
      const speaker = selectedSpeaker;
      const line = asAI ? '' : userResponse; // Empty string for AI, or user's text
      
      await apiService.continueDialogue(caseId, speaker, line);
      
      // Reload case data
      await loadCaseData();
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have permission to modify this case.');
      } else {
        setError(err.response?.data?.error || 'Failed to continue dialogue');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsContinuing(false);
    }
  };

  // Load version history
  const handleViewVersionHistory = async () => {
    if (!caseId) return;
    
    try {
      setLoadingVersions(true);
      const versionHistory = await apiService.getCaseVersions(caseId);
      setVersions(versionHistory);
      setShowVersionHistory(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  // Rollback to version
  const handleRollback = async (versionNumber: number) => {
    if (!caseId) return;
    
    try {
      setLoadingVersions(true);
      await apiService.rollbackToVersion(caseId, versionNumber);
      await loadCaseData();
      setShowVersionHistory(false);
      setVersions([]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to rollback');
    } finally {
      setLoadingVersions(false);
    }
  };

  // Only show full error page for critical errors (404, network errors, etc.)
  // Permission errors (403) will be shown as inline banner on the page
  if ((error && !permissionError) || (!caseData && !permissionError)) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Case</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/cases')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Cases
          </button>
        </div>
      </div>
    );
  }
  
  // If there's a permission error but no case data, show warning without case details
  if (permissionError && !caseData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/cases')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Cases
        </button>
        
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Insufficient Permissions</h3>
              <p className="mt-1 text-sm text-yellow-700">{permissionError}</p>
              <p className="mt-2 text-sm text-yellow-600">
                You do not have permission to view this case. Contact your administrator if you need access.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // At this point, caseData must exist (TypeScript guard)
  if (!caseData) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Permission error banner - shown inline on the page */}
      {permissionError && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Insufficient Permissions</h3>
              <p className="mt-1 text-sm text-yellow-700">{permissionError}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Read-only mode banner */}
      {isViewOnly && (
        <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Viewing in read-only mode.</span> You can view this student's case and create a template from it, but you cannot modify their work.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/cases')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Cases
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{caseData.title}</h1>
            <p className="mt-2 text-gray-600">
              {caseData.patientProfile.name}, {caseData.patientProfile.age} | {caseData.patientProfile.diagnosis}
            </p>
          </div>
          <div className="flex space-x-3">
            {/* HIDDEN: Guided Learning, Questions, Audio, Auto Rate buttons */}
            {/* 
            <button
              onClick={() => navigate(`/guided-learning/${caseId}`)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
              Start Guided Learning
            </button>
            <button
              onClick={handleGenerateQuestions}
              disabled={isGeneratingQuestions || !dialogue}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {isGeneratingQuestions ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <AcademicCapIcon className="h-4 w-4 mr-2" />
              )}
              Generate Questions
            </button>
            <button
              onClick={handleGenerateAudio}
              disabled={isGeneratingAudio || !dialogue}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {isGeneratingAudio ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <SpeakerWaveIcon className="h-4 w-4 mr-2" />
              )}
              Generate Audio
            </button>
            <button
              onClick={handleAutoRate}
              disabled={isAutoRating || !dialogue || ratings.length > 0}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {isAutoRating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              ) : (
                <StarIcon className="h-4 w-4 mr-2" />
              )}
              {ratings.length > 0 ? 'Already Rated' : 'Auto Rate'}
            </button>
            */}
            <button
              onClick={handleViewVersionHistory}
              disabled={loadingVersions}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {loadingVersions ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              ) : (
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              Version History
            </button>
          </div>
        </div>
      </div>

      {/* Continue Dialogue Modal */}
      {showContinueModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowContinueModal(false)}></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {continueAsAI ? '🤖 AI Will Generate Response' : '✏️ Write Your Response'}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {continueAsAI 
                    ? 'The AI will automatically generate the next response based on the case context and dialogue history.' 
                    : 'Type your response below. This will be added to the dialogue.'}
                </p>
                {!continueAsAI && (
                  <textarea
                    value={userResponse}
                    onChange={(e) => setUserResponse(e.target.value)}
                    rows={4}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Type your response..."
                  />
                )}
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  onClick={() => handleContinueDialogue(continueAsAI)}
                  disabled={isContinuing || (!continueAsAI && !userResponse.trim())}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                >
                  {isContinuing ? 'Processing...' : (continueAsAI ? 'Generate AI Response' : 'Add Response')}
                </button>
                <button
                  onClick={() => {
                    setShowContinueModal(false);
                    setUserResponse('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionHistory && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowVersionHistory(false)}></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Version History
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {versions.map((version) => (
                    <div key={version.versionNumber} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold">Version {version.versionNumber}</span>
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                              {version.changeType.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{version.changeDescription}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(version.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRollback(version.versionNumber)}
                          disabled={loadingVersions}
                          className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-5">
                <button
                  onClick={() => setShowVersionHistory(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Case Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Case Information</h2>
              <div className="flex gap-2">
                {!isViewOnly && (
                  <button
                    onClick={handleEditCaseInfo}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                  </button>
                )}
                {isInstructor && (
                  <button
                    onClick={handleMakeTemplate}
                    disabled={isPublishing}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                  >
                    {isPublishing ? 'Creating...' : '📋 Make Template'}
                  </button>
                )}
                <button
                  onClick={handleToggleGoldExample}
                  disabled={isTogglingGold}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md ${
                    caseData.isGoldExample
                      ? 'border border-yellow-400 text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                      : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  } disabled:opacity-50`}
                >
                  {caseData.isGoldExample ? '⭐ Gold Example' : '☆ Mark as Gold Example'}
                </button>
                
                {/* Learner Level Selector */}
                <div className="relative inline-block">
                  <select
                    value={caseData.learnerLevel || ''}
                    onChange={(e) => handleSetLearnerLevel(e.target.value)}
                    disabled={isSettingLevel}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No Level</option>
                    <option value="BEGINNER">🌱 Beginner</option>
                    <option value="INTERMEDIATE">📚 Intermediate</option>
                    <option value="ADVANCED">🎓 Advanced</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Scenario</h3>
                <p className="text-gray-600">{caseData.scenario}</p>
              </div>
              
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
                <h3 className="font-semibold text-amber-800 mb-2">Family Statement</h3>
                <p className="text-amber-700 italic">"{caseData.familyStatement}"</p>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-2">Clinician Objectives</h3>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                {caseData.clinicianObjectives.map((objective, index) => (
                  <li key={index}>{objective}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Dialogue */}
          {dialogue && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Dialogue Simulation</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleAnalyzeAllEmotions}
                  disabled={isAnalyzingAllEmotions}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAnalyzingAllEmotions ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {analysisProgress.total > 0 ? 
                        `Analyzing... (${analysisProgress.current}/${analysisProgress.total})` : 
                        'Analyzing...'
                      }
                    </>
                  ) : (
                    <>
                      <HeartIcon className="h-4 w-4 mr-2" />
                      Analyze All Emotions
                    </>
                  )}
                </button>
              </div>
            </div>
              
              {/* Progress bar for bulk analysis */}
              {isAnalyzingAllEmotions && analysisProgress.total > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Analyzing emotions...</span>
                    <span>{analysisProgress.current} of {analysisProgress.total} turns</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-pink-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(analysisProgress.current / analysisProgress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                {dialogue.turns?.map((turn, index) => {
                  // Find the analysis result for this turn using turnNumber
                  const analysisResult = bulkAnalysisResults.find(result => result.turnIndex === turn.turnNumber);
                  
                  return (
                    <DialogueBubble
                      key={turn.id}
                      turn={turn}
                      participants={dialogue.participants}
                      turnIndex={turn.turnNumber}
                      caseId={caseId!}
                      onModifyDialogue={handleModifyDialogue}
                      isAnalyzed={analyzedTurns.has(turn.turnNumber)}
                      emotionAnalysis={analysisResult?.emotionAnalysis || null}
                      totalTurns={dialogue.turns?.length || 0}
                    />
                  );
                })}
              </div>

              {/* Continue Dialogue Section - Inline (only for owners) */}
              {!isViewOnly && dialogue.turns && dialogue.turns.length > 0 && (
                <div className="mt-6 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-dashed border-green-300">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Continue the Conversation</h3>
                  
                  {/* Step 1: Select Speaker */}
                  {responseMode === 'select' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Who speaks next? <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedSpeaker}
                          onChange={(e) => setSelectedSpeaker(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a participant...</option>
                          {dialogue.participants?.map((participant, idx) => (
                            <option key={idx} value={participant.name}>
                              {participant.name} ({participant.role})
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={() => {
                          if (selectedSpeaker) {
                            setResponseMode('method');
                          }
                        }}
                        disabled={!selectedSpeaker}
                        className="w-full px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next →
                      </button>
                    </div>
                  )}

                  {/* Step 2: Choose Response Method */}
                  {responseMode === 'method' && (
                    <div className="space-y-4">
                      <div className="mb-3">
                        <p className="text-sm text-gray-600">
                          Selected: <span className="font-semibold text-gray-900">{selectedSpeaker}</span>
                        </p>
                      </div>
                      <p className="text-sm font-medium text-gray-700 mb-3">How should they respond?</p>
                      
                      <button
                        onClick={() => setResponseMode('ai')}
                        className="w-full p-4 text-left border-2 border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
                      >
                        <div className="flex items-center">
                          <SparklesIcon className="h-6 w-6 text-green-600 mr-3" />
                          <div>
                            <p className="font-semibold text-gray-900">🤖 AI Generate</p>
                            <p className="text-sm text-gray-600">Let AI write the response</p>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => setResponseMode('manual')}
                        className="w-full p-4 text-left border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
                      >
                        <div className="flex items-center">
                          <PencilIcon className="h-6 w-6 text-blue-600 mr-3" />
                          <div>
                            <p className="font-semibold text-gray-900">✏️ Write Myself</p>
                            <p className="text-sm text-gray-600">I'll write the response</p>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => {
                          setResponseMode('select');
                          setSelectedSpeaker('');
                        }}
                        className="w-full px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                      >
                        ← Back
                      </button>
                    </div>
                  )}

                  {/* Step 3a: AI Generate */}
                  {responseMode === 'ai' && (
                    <div className="space-y-4">
                      <div className="mb-3">
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold text-gray-900">{selectedSpeaker}</span> will respond (AI Generated)
                        </p>
                      </div>
                      
                      {isContinuing ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                          <p className="text-sm text-gray-600">AI is generating a response based on:</p>
                          <ul className="text-xs text-gray-500 mt-2 space-y-1">
                            <li>• Previous dialogue history</li>
                            <li>• Case context and objectives</li>
                            <li>• Speaker's role and perspective</li>
                          </ul>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setResponseMode('method');
                            }}
                            className="px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          >
                            ← Back
                          </button>
                          <button
                            onClick={async () => {
                              setIsContinuing(true);
                              await handleContinueDialogue(true);
                              setIsContinuing(false);
                              setResponseMode('select');
                              setSelectedSpeaker('');
                            }}
                            className="flex-1 px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                          >
                            ✓ Generate & Add
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 3b: Manual Write */}
                  {responseMode === 'manual' && (
                    <div className="space-y-4">
                      <div className="mb-3">
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold text-gray-900">{selectedSpeaker}</span> - Your Response
                        </p>
                      </div>
                      
                      <textarea
                        value={userResponse}
                        onChange={(e) => setUserResponse(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder={`Write ${selectedSpeaker}'s response here...`}
                        disabled={isContinuing}
                      />
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setResponseMode('method');
                            setUserResponse('');
                          }}
                          disabled={isContinuing}
                          className="px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                        >
                          ← Back
                        </button>
                        <button
                          onClick={async () => {
                            setIsContinuing(true);
                            await handleContinueDialogue(false);
                            setIsContinuing(false);
                            setUserResponse('');
                            setResponseMode('select');
                            setSelectedSpeaker('');
                          }}
                          disabled={isContinuing || !userResponse.trim()}
                          className="flex-1 px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                          {isContinuing ? 'Adding...' : '✓ Add Response'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Audio Player */}
          {currentAudioUrl && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                🔊 Audio Conversation
                {audioFiles.length > 1 && (
                  <span className="text-sm text-gray-500 ml-2">
                    ({audioFiles.length} versions for different speaker combinations)
                  </span>
                )}
              </h2>
              
              {/* Multiple Audio Options */}
              {audioFiles.length > 1 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-3">
                    💡 This case has 3+ participants. We generated separate audio for each 2-speaker combination:
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    {audioFiles.map((file, index) => {
                      // Extract speaker names from filename
                      const speakerNames = file.fileName
                        .split('_')
                        .slice(0, -2) // Remove timestamp and extension
                        .join(' & ') || `Option ${index + 1}`;
                      
                      return (
                        <button
                          key={file.id}
                          onClick={() => handleSwitchAudio(index)}
                          className={`px-4 py-2 rounded-lg text-left transition-colors ${
                            currentAudioIndex === index
                              ? 'bg-blue-100 border-2 border-blue-500 text-blue-900 font-medium'
                              : 'bg-gray-50 border border-gray-300 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className="text-sm">
                            {currentAudioIndex === index && '▶ '}
                            {speakerNames}
                            <span className="text-xs text-gray-500 ml-2">
                              ({Math.round(file.durationSeconds)}s)
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <AudioPlayer
                audioUrl={currentAudioUrl}
                isPlaying={isPlaying}
                onPlay={handlePlayAudio}
                onPause={handlePlayAudio}
                onStop={handleStopAudio}
              />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Ratings */}
          {ratings.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ratings</h3>
              <div className="space-y-4">
                {ratings.map((rating) => (
                  <div key={rating.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {rating.user?.name || 'Anonymous'}
                      </span>
                      <span className="text-sm text-gray-500">
                        {new Date(rating.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center mb-2">
                      <span className="text-2xl font-bold text-blue-600">
                        {rating.overallScore}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">/10</span>
                    </div>
                    <p className="text-sm text-gray-600">{rating.overallSummary}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Improve Dialogue */}
          {latestRating && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Improve This Dialogue</h3>
              <p className="text-sm text-gray-600 mb-4">
                You can add your own notes below. The AI will use these notes and the feedback above to regenerate the conversation.
              </p>
              <textarea
                value={improveNotes}
                onChange={(e) => setImproveNotes(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Optional: Add specific instructions here..."
              />
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleImproveDialogue}
                  disabled={isImprovingDialogue}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isImprovingDialogue ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      <span>Improving...</span>
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="h-4 w-4 mr-2" />
                      <span>Improve Dialogue</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Case Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Case Details</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Patient Name</dt>
                <dd className="text-sm text-gray-900">{caseData.patientProfile.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Age</dt>
                <dd className="text-sm text-gray-900">{caseData.patientProfile.age}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Weight</dt>
                <dd className="text-sm text-gray-900">{caseData.patientProfile.weight}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="text-sm text-gray-900">
                  {new Date(caseData.createdAt).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created By</dt>
                <dd className="text-sm text-gray-900">{caseData.createdBy?.name || 'Unknown'}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Edit Case Info Modal */}
      {showEditCaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Edit Case Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={editedCaseInfo.title}
                    onChange={(e) => setEditedCaseInfo({ ...editedCaseInfo, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scenario</label>
                  <textarea
                    value={editedCaseInfo.scenario}
                    onChange={(e) => setEditedCaseInfo({ ...editedCaseInfo, scenario: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Family Statement</label>
                  <textarea
                    value={editedCaseInfo.familyStatement}
                    onChange={(e) => setEditedCaseInfo({ ...editedCaseInfo, familyStatement: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Patient Profile</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Name</label>
                      <input
                        type="text"
                        value={editedCaseInfo.patientProfile.name || ''}
                        onChange={(e) => setEditedCaseInfo({ 
                          ...editedCaseInfo, 
                          patientProfile: { ...editedCaseInfo.patientProfile, name: e.target.value }
                        })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Age</label>
                      <input
                        type="text"
                        value={editedCaseInfo.patientProfile.age || ''}
                        onChange={(e) => setEditedCaseInfo({ 
                          ...editedCaseInfo, 
                          patientProfile: { ...editedCaseInfo.patientProfile, age: e.target.value }
                        })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Weight</label>
                      <input
                        type="text"
                        value={editedCaseInfo.patientProfile.weight || ''}
                        onChange={(e) => setEditedCaseInfo({ 
                          ...editedCaseInfo, 
                          patientProfile: { ...editedCaseInfo.patientProfile, weight: e.target.value }
                        })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Diagnosis</label>
                      <input
                        type="text"
                        value={editedCaseInfo.patientProfile.diagnosis || ''}
                        onChange={(e) => setEditedCaseInfo({ 
                          ...editedCaseInfo, 
                          patientProfile: { ...editedCaseInfo.patientProfile, diagnosis: e.target.value }
                        })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Clinician Objectives</label>
                  {editedCaseInfo.clinicianObjectives.map((obj, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={obj}
                        onChange={(e) => {
                          const newObjs = [...editedCaseInfo.clinicianObjectives];
                          newObjs[idx] = e.target.value;
                          setEditedCaseInfo({ ...editedCaseInfo, clinicianObjectives: newObjs });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                      />
                      <button
                        onClick={() => {
                          const newObjs = editedCaseInfo.clinicianObjectives.filter((_, i) => i !== idx);
                          setEditedCaseInfo({ ...editedCaseInfo, clinicianObjectives: newObjs });
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setEditedCaseInfo({ 
                        ...editedCaseInfo, 
                        clinicianObjectives: [...editedCaseInfo.clinicianObjectives, '']
                      });
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Add Objective
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowEditCaseModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCaseInfo}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Questions Modal */}
      <QuestionsModal
        isOpen={showQuestionsModal}
        onClose={handleCloseQuestionsModal}
        questions={generatedQuestions}
        isLoading={isGeneratingQuestions}
        caseTitle={caseData.title}
      />


    </div>
  );
};

export default CaseDetailPage;
